import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import OrthoPrintSummary from '../components/OrthoPrintSummary.js';
import { useNavigate } from 'react-router-dom';
import REGIONAL_CONFIG, { ZONE_META, REGION_META } from './ortho/data/regionalConfig.js';
import {
  PAIN_BEHAVIOR_OPTIONS, PAIN_DISTRIBUTION_OPTIONS, SYMPTOM_PROGRESSION_OPTIONS,
  TRAUMA_SEVERITY_OPTIONS, INFLAMMATION_SYMPTOMS_OPTIONS, INSTABILITY_SYMPTOMS_OPTIONS,
  MORNING_FUNCTION_OPTIONS, PREVIOUS_HISTORY_OPTIONS, PAIN_IRRITABILITY_OPTIONS,
  FUNCTIONAL_IMPACT_OPTIONS, NATURE_OPTIONS, PALPATION_OPTIONS, GAIT_OPTIONS, UMN_OPTIONS
} from './ortho/data/clinicalOptions.js';
import {
  theme, styles, STEPS, API_BASE_URL, MAX_CHAT_HISTORY, normalizeList,
  SUBJECTIVE_ORTHO_DRAFT_KEY, SAVED_ASSESSMENTS_KEY, formatAiNotes, parseApiResponse,
  DEFAULT_FORM_DATA, renderAiNotes
} from './ortho/constants.js';
import { runClinicalEngine } from './ortho/utils/clinicalEngine.js';
import FormInput from './ortho/components/FormInput.js';
import SmartRow from './ortho/components/SmartRow.js';
import { getAuthHeaders, userKey } from '../utils/auth.js';
import { pushAssessments } from '../utils/syncAssessments.js';
import { payForDiagnosis } from '../utils/payDiagnosis.js';

const UnifiedOrthoForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [assessmentId, setAssessmentId] = useState(null);
  const [savedAssessments, setSavedAssessments] = useState([]);
  
  const [activeZone, setActiveZone] = useState(''); 
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [payGate, setPayGate] = useState(null);
  const [payGateError, setPayGateError] = useState(null);
  const [hoveredZone, setHoveredZone] = useState(''); 
  const [draftReady, setDraftReady] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [validationModal, setValidationModal] = useState(null);
  const [showInvestigations, setShowInvestigations] = useState(false);
  const [showNeuro, setShowNeuro] = useState(false);
  const [diagTab, setDiagTab] = useState('ai');
  const [aiAutoFilled, setAiAutoFilled] = useState(false);

  /* ── Gate: redirect to /home if at limit and no prepaid/reopened draft ── */
  useEffect(() => {
    try {
      const draft = JSON.parse(window.localStorage.getItem(userKey(SUBJECTIVE_ORTHO_DRAFT_KEY)) || 'null');
      if (draft?.assessmentId) return;
    } catch {}
    fetch(`${API_BASE_URL}/api/user/usage`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(u => { if (u && !u.isAdmin && u.tier === 'free' && u.used >= u.limit) navigate('/home'); })
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    try {
      const savedList = window.localStorage.getItem(userKey(SAVED_ASSESSMENTS_KEY));
      if (savedList) setSavedAssessments(JSON.parse(savedList));

      const savedDraft = window.localStorage.getItem(userKey(SUBJECTIVE_ORTHO_DRAFT_KEY));
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        if (typeof parsed.currentStep === 'number' && parsed.currentStep >= 0) setCurrentStep(parsed.currentStep);
        else setCurrentStep(0);
        
        if (typeof parsed.activeZone === 'string') setActiveZone(parsed.activeZone);
        if (typeof parsed.isSubmitted === 'boolean') setIsSubmitted(parsed.isSubmitted);
        if (Array.isArray(parsed.chatHistory)) setChatHistory(parsed.chatHistory);
        if (typeof parsed.chatInput === 'string') setChatInput(parsed.chatInput);
        if (parsed.assessmentId) setAssessmentId(parsed.assessmentId);
        if (parsed.formData && typeof parsed.formData === 'object') {
          setFormData({ ...DEFAULT_FORM_DATA, ...parsed.formData });
        }
      } else {
        setCurrentStep(0);
      }
    } catch (error) {
      console.error('Unable to restore draft:', error);
    } finally {
      setDraftReady(true);
    }
  }, []);

  useEffect(() => {
    if (!draftReady) return;
    try {
      window.localStorage.setItem(userKey(SUBJECTIVE_ORTHO_DRAFT_KEY), JSON.stringify({
        currentStep, activeZone, isSubmitted, chatHistory, chatInput, formData, assessmentId
      }));
    } catch (error) {
      console.error('Unable to save draft:', error);
    }
  }, [draftReady, currentStep, activeZone, isSubmitted, chatHistory, chatInput, formData, assessmentId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (e.target.tagName === 'TEXTAREA' && name === 'hpiText' && value.endsWith('\n') && !value.endsWith('• ')) {
       newValue = value + '• ';
    }
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const addRow = (listName, defaultObj) => setFormData(prev => ({ ...prev, [listName]: [...prev[listName], { id: Date.now(), ...defaultObj }] }));
  const updateRow = (listName, id, updates) => setFormData(prev => ({ ...prev, [listName]: prev[listName].map(row => row.id === id ? { ...row, ...updates } : row) }));
  const deleteRow = (listName, id) => setFormData(prev => ({ ...prev, [listName]: prev[listName].filter(row => row.id !== id) }));
  
  const toggleArrayItem = (arrayName, item) => {
    setFormData(prev => {
      const current = prev[arrayName];
      return { ...prev, [arrayName]: current.includes(item) ? current.filter(i => i !== item) : [...current, item] };
    });
  };

  const handleNext = () => {
    if (currentStep === 1 && !formData.name.trim()) {
       alert("Patient Name is required before proceeding.");
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const currentStepRef = useRef(currentStep);
  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);
  useEffect(() => {
    window.history.pushState(null, '');
    const onPopState = () => {
      if (currentStepRef.current > 0) {
        window.history.pushState(null, '');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setCurrentStep(s => Math.max(s - 1, 0));
      } else {
        navigate('/home');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [navigate]);

  const handleSaveAndReturnHome = () => {
    const record = {
      id: assessmentId || Date.now(),
      date: new Date().toLocaleDateString(),
      patientName: formData.name || 'Unnamed Patient',
      zone: activeZone || 'Unspecified Zone',
      diagnosis: typeof formData.diagnosis === 'string' ? formData.diagnosis.substring(0, 50) + '...' : (formData.diagnosis?.primary || 'No diagnosis yet'),
      data: formData,
      isSubmitted,
      chatHistory
    };

    const existingIndex = savedAssessments.findIndex(a => a.id === record.id);
    let updatedList;
    if (existingIndex >= 0) {
      updatedList = [...savedAssessments];
      updatedList[existingIndex] = record;
    } else {
      updatedList = [record, ...savedAssessments];
    }

    setSavedAssessments(updatedList);
    window.localStorage.setItem(userKey(SAVED_ASSESSMENTS_KEY), JSON.stringify(updatedList));
    pushAssessments('saved-ortho-assessments', updatedList);

    setFormData(DEFAULT_FORM_DATA);
    setActiveZone('');
    setIsSubmitted(false);
    setChatHistory([]);
    setAssessmentId(null);
    window.localStorage.removeItem(userKey(SUBJECTIVE_ORTHO_DRAFT_KEY));

    navigate('/home');
  };

  const buildAssessmentSummary = () => ({
    patient: { name: formData.name, age: formData.age, gender: formData.gender, occupation: formData.occupation },
    zone: activeZone,
    subjective: {
      chiefComplaint: formData.chiefComplaint, hpi: formData.hpiText, painSeverity: formData.symSeverity, onset: formData.symOnset, durationWeeks: formData.symDurationWeeks,
      painNature: formData.symNature, mechanismOfInjury: formData.mechanismOfInjury, aggravating: formData.aggravating,
      relieving: formData.relieving, redFlags: formData.redFlags, functionalLimitations: formData.functionalLimitations
    },
    objective: {
      observations: formData.observations, palpation: formData.palpation, gait: formData.gait, rom: formData.rom_list, mmt: formData.mmt_list,
      jointGlides: formData.glide_list, neurology: formData.neuro_list, specialTests: formData.special_list, functionalTests: formData.functional_list
    },
    investigations: formData.investigation_list
  });

  const handleChatQuery = async () => {
    const question = chatInput.trim();
    if (!question || chatLoading) return;

    const nextHistory = [...chatHistory, { role: "user", text: question }];
    const trimmedHistory = nextHistory.slice(-MAX_CHAT_HISTORY);
    setChatHistory(trimmedHistory);
    setChatInput("");
    setChatLoading(true);

    try {
      // If a sessionId exists the server already has the assessment cached —
      // only send the question and recent history (saves ~400-1,200 input tokens).
      const aid = assessmentId || crypto.randomUUID();
      if (!assessmentId) setAssessmentId(aid);
      const body = chatSessionId
        ? { question, messages: trimmedHistory.slice(-4), sessionId: chatSessionId, assessmentId: aid }
        : { question, zone: activeZone, assessment: buildAssessmentSummary(), diagnosis: formData.diagnosis || null, messages: trimmedHistory.slice(-4), assessmentId: aid };

      const response = await fetch(`${API_BASE_URL}/api/chat-case`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      const result = await parseApiResponse(response);
      if (response.status === 401) { navigate('/app'); return; }
      if (!response.ok) throw new Error(result.error || "AI chat failed.");

      if (result._sessionExpired) setChatSessionId(null); // session expired — server reset, clear local id
      if (result.sessionId && !chatSessionId) setChatSessionId(result.sessionId);
      setChatHistory([...trimmedHistory, { role: "ai", text: formatAiNotes(result.answer || "No response generated.") }].slice(-MAX_CHAT_HISTORY));
    } catch (error) {
      console.error("AI Chat Error:", error);
      setChatHistory([...trimmedHistory, { role: "ai", text: "I couldn't generate a response just now. Please check the backend server and try again." }].slice(-MAX_CHAT_HISTORY));
    } finally {
      setChatLoading(false);
    }
  };

  const checkOrthoRequirements = () => {
    const missing = [];
    if (!activeZone?.trim()) missing.push("Body region (zone) must be selected");
    if (!formData.chiefComplaint?.trim()) missing.push("Chief complaint is required");
    const hasROM     = Array.isArray(formData.rom_list)      && formData.rom_list.length > 0;
    const hasMMT     = Array.isArray(formData.mmt_list)      && formData.mmt_list.length > 0;
    const hasSpecial = Array.isArray(formData.special_list)  && formData.special_list.length > 0;
    if (!hasROM && !hasMMT && !hasSpecial)
      missing.push("At least one objective finding is required: Range of Motion, Manual Muscle Testing, or Special Orthopaedic Tests");
    return missing;
  };

  const handleFinalSubmit = async (e) => {
    e?.preventDefault?.();
    const missing = checkOrthoRequirements();
    if (missing.length > 0) { setValidationModal(missing); return; }
    const clinicalEngineOutput = runClinicalEngine(formData);
    const aiPayload = {
      patient: { name: formData.name, age: formData.age, gender: formData.gender, occupation: formData.occupation, dominantHand: formData.dominantHand, activityLevel: formData.activityLevel },
      history: { medicalHistory: formData.historyMedical, personalHistory: formData.historyPersonal, orthopedicHistory: formData.previousHistory },
      symptoms: { chiefComplaint: formData.chiefComplaint, hpi: formData.hpiText, side: formData.symSide, onset: formData.symOnset, progression: formData.symptomProgression, durationWeeks: formData.symDurationWeeks, morningFunction: formData.morningFunction, inflammationIndicators: formData.inflammationSymptoms, instabilitySymptoms: formData.instabilitySymptoms },
      painCharacteristics: { location: formData.symLocation, nature: formData.symNature, severityVAS: formData.symSeverity, distribution: formData.painDistribution, behaviorPattern: formData.painBehavior, irritability: formData.painIrritability },
      painBehaviour: { aggravatingFactors: formData.aggravating, relievingFactors: formData.relieving },
      mechanismOfInjury: { description: formData.mechanismOfInjury, traumaSeverity: formData.traumaSeverity },
      redFlags: formData.redFlags,
      functionalImpact: { overallLimitation: formData.functionalImpact, specificTasks: formData.functionalLimitations },
      objective: { observations: formData.observations, gaitAnalysis: formData.gait, palpation: formData.palpation, rom: formData.rom_list, mmt: formData.mmt_list, jointGlides: formData.glide_list, neurology: { dermatomeMyotomeReflex: formData.neuro_list, umnSigns: formData.umnSigns }, functionalPerformanceTests: formData.functional_list, specialOrthopedicTests: formData.special_list },
      investigations: formData.investigation_list,
    };

    try {
      setIsDiagnosing(true);
      const aid = assessmentId || crypto.randomUUID();
      if (!assessmentId) setAssessmentId(aid);
      const response = await fetch(`${API_BASE_URL}/api/diagnose`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          assessmentId: aid,
          zone: activeZone,
          rawData: aiPayload,
          clinicalSignals: clinicalEngineOutput.signals,
          differentials: clinicalEngineOutput.differentials,
          suggestedTreatment: clinicalEngineOutput.treatment
        })
      });

      const result = await response.json();
      if (response.status === 401) { navigate('/app'); return; }
      if (response.status === 402 && result.pay_required) {
        setPayGate({ assessmentId: result.assessmentId || aid });
        return;
      }
      if (result.status === "insufficient_data") {
        const list = (result.missing || []).map(m => `\n• ${m}`).join("");
        throw new Error(`Cannot generate diagnosis — incomplete assessment:${list}\n\nPlease complete these sections and retry.`);
      }
      if (result.status === "contradictory_data") {
        const list = (result.issues || []).map(i => `\n• ${i}`).join("");
        throw new Error(`Conflicting clinical findings detected — please review before diagnosing:${list}`);
      }
      if (!response.ok) throw new Error(result.error || "AI diagnosis failed.");
      if (result.status === "partial" || !result.diagnosis)
        throw new Error(result.message || "Diagnosis incomplete. Please add more clinical detail and retry.");

      const diag = result.diagnosis;
      setFormData(prev => {
        const d = typeof diag === 'object' && diag ? diag : {};
        return {
          ...prev,
          diagnosis: diag,
          differentialDiagnosis: prev.differentialDiagnosis || (Array.isArray(d.differentials) ? d.differentials.join(', ') : ''),
          shortTermGoals: prev.shortTermGoals || (Array.isArray(d.shortTermGoals) ? d.shortTermGoals.join('\n') : (typeof d.shortTermGoals === 'string' ? d.shortTermGoals : '')),
          longTermGoals: prev.longTermGoals || (Array.isArray(d.longTermGoals) ? d.longTermGoals.join('\n') : (typeof d.longTermGoals === 'string' ? d.longTermGoals : '')),
          treatmentPlan: prev.treatmentPlan || (Array.isArray(d.physiotherapyManagementPlan) ? d.physiotherapyManagementPlan.join('\n• ') : ''),
        };
      });
      setIsSubmitted(true);
      setAiAutoFilled(true);
    } catch (error) {
      console.error("AI Error:", error);
      alert(error.message || "Error connecting to AI Diagnostic Engine.");
    } finally {
      setIsDiagnosing(false);
    }
  };

  const handleRefineDiagnosis = async () => {
    const refinementContext = prompt("Add additional clinical context or ask for clarification:");
    if (!refinementContext?.trim()) return;

    setIsDiagnosing(true);
    try {
      const aid = assessmentId || crypto.randomUUID();
      if (!assessmentId) setAssessmentId(aid);
      const response = await fetch(`${API_BASE_URL}/api/refine-diagnosis`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          assessmentId: aid,
          zone: activeZone,
          previousDiagnosis: formData.diagnosis,
          additionalContext: refinementContext,
          assessment: buildAssessmentSummary()
        })
      });

      const result = await parseApiResponse(response);
      if (result.status === "insufficient_data") {
        const list = (result.missing || []).map(m => `\n• ${m}`).join("");
        throw new Error(`Cannot refine diagnosis — incomplete assessment:${list}\n\nPlease complete these sections and retry.`);
      }
      if (result.status === "contradictory_data") {
        const list = (result.issues || []).map(i => `\n• ${i}`).join("");
        throw new Error(`Conflicting clinical findings detected — please review before refining:${list}`);
      }
      if (!response.ok) throw new Error(result.error || "Refinement failed");
      if (result.status === "partial" || !result.diagnosis)
        throw new Error(result.message || "Refinement incomplete. Please try again.");

      setFormData(prev => ({ ...prev, diagnosis: result.diagnosis }));
    } catch (error) {
      console.error("Refinement Error:", error);
      alert(error.message || "Failed to refine diagnosis. Please try again.");
    } finally {
      setIsDiagnosing(false);
    }
  };


  const ZoneConfig = REGIONAL_CONFIG[activeZone] || { redFlags: [], functionalLimitations: [], moiOptions: [], aggravatingOptions: [], relievingOptions: [], observations: [], palpationOptions: [], movements: [], specialTests: [] };
  
  const diagnosisSource = typeof formData.diagnosis === 'string'
    ? { primary: formData.diagnosis, differentials: [], clinicalReasoning: [], physiotherapyManagementPlan: [], keyExercises: [], redFlags: [] }
    : (formData.diagnosis || {});
    
  const diagnosisData = {
    primary:                     diagnosisSource.primary || '',
    differentials:               normalizeList(diagnosisSource.differentials),
    clinicalReasoning:           normalizeList(diagnosisSource.clinicalReasoning),
    treatmentPhase:              diagnosisSource.treatmentPhase || '',
    physiotherapyManagementPlan: normalizeList(diagnosisSource.physiotherapyManagementPlan),
    keyExercises:                normalizeList(diagnosisSource.keyExercises),
    shortTermGoals:              normalizeList(diagnosisSource.shortTermGoals),
    longTermGoals:               normalizeList(diagnosisSource.longTermGoals),
    precautions:                 normalizeList(diagnosisSource.precautions),
    patientEducation:            normalizeList(diagnosisSource.patientEducation),
    outcomeMeasures:             normalizeList(diagnosisSource.outcomeMeasures),
    prognosisTimeline:           diagnosisSource.prognosisTimeline || '',
    redFlags:                    normalizeList(diagnosisSource.redFlags),
    contraindications:           normalizeList(diagnosisSource.contraindications),
  };

  return (
    <>
    <div style={styles.appWrapper}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        input, select, textarea, button { font-family: inherit; }
        ::placeholder { color: #94a3b8; opacity: 1; }
        button:active { opacity: 0.72; transform: scale(0.96); transition: transform 0.07s, opacity 0.07s !important; }
        select { appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 36px !important; }
        @media (max-width: 480px) {
          div[style*="repeat(auto-fit"] { grid-template-columns: 1fr !important; }
          input:not([class]), select { min-height: 48px !important; }
        }
        @media (max-width: 375px) {
          .ortho-step-label { display: none !important; }
          .ortho-step-node { min-width: 28px !important; min-height: 28px !important; }
          .ortho-content-pad { padding: 0 8px !important; }
          div[style*="padding: '20px 16px 96px'"] { padding-bottom: 80px !important; }
        }
        @media (max-width: 360px) {
          .ortho-step-node { min-width: 26px !important; min-height: 26px !important; font-size: 0.7rem !important; }
          .ortho-nav-title { font-size: 0.75rem !important; }
        }
        @page { margin: 0.5cm; }
        @media print {
          body > *:not(#ortho-print-summary) { display: none !important; }
          #ortho-print-summary { display: block !important; padding: 24px; background: #fff; font-family: 'Inter', sans-serif; font-size: 11px; color: #0f172a; }
          #ortho-print-summary > * { page-break-inside: avoid; }
        }
        @media screen { #ortho-print-summary { display: none; } }
        @media (max-width: 560px) {
          .ortho-step-label { display: none !important; }
          .ortho-step-node { min-width: 32px !important; }
          .ortho-nav-title { font-size: 0.82rem !important; }
          .ortho-zone-badge { display: none !important; }
          .ortho-content-pad { padding: 0 10px !important; }
          .sr-rom-row input[type="number"] { width: 48px !important; }
          /* Subjective page */
          .subj-fl-header { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .subj-fl-header select { width: 100% !important; }
          .subj-section-gap { margin-top: 20px !important; }
        }
        @media (max-width: 520px) {
          /* Zone selector — all 9 zones on one screen */
          .ortho-zone-title { font-size: 0.88rem !important; margin-bottom: 10px !important; }
          .ortho-region-group { margin-bottom: 8px !important; }
          .ortho-region-hdr { margin-bottom: 5px !important; gap: 8px !important; }
          .ortho-region-hdr-icon { width: 24px !important; height: 24px !important; min-width: 24px !important; font-size: 13px !important; border-radius: 7px !important; }
          .ortho-region-hdr-title { font-size: 0.8rem !important; }
          .ortho-zone-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 5px !important; padding-bottom: 0 !important; }
          .ortho-zone-card { flex-direction: row !important; padding: 6px 7px !important; gap: 6px !important; align-items: center !important; border-radius: 10px !important; }
          .zone-icon-box { width: 26px !important; height: 26px !important; min-width: 26px !important; border-radius: 7px !important; font-size: 13px !important; margin-bottom: 0 !important; flex-shrink: 0 !important; }
          .zone-name { font-size: 0.68rem !important; line-height: 1.2 !important; }
          .zone-areas { display: none !important; }
        }
        .ai-card-pre { padding: 20px 0 28px; text-align: center; }
        .ai-pre-title { margin: 0 0 10px; color: #1e40af; font-size: 1.3rem; font-weight: 800; }
        .ai-pre-desc { color: #1d4ed8; margin: 0 0 20px; font-size: 1rem; font-weight: 500; line-height: 1.6; }
        .ai-analyze-btn { padding: 16px 32px; font-size: 1.1rem; }
        .ai-results-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; margin-bottom: 25px; border-bottom: 1px solid #e2e8f0; padding-bottom: 15px; }
        .ai-grid-2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 16px; margin-bottom: 20px; }
        @media (max-width: 600px) {
          .ai-card-pre { padding: 10px 0 18px; }
          .ai-pre-title { font-size: 1.05rem !important; }
          .ai-pre-desc { font-size: 0.88rem !important; margin-bottom: 14px !important; }
          .ai-analyze-btn { padding: 14px 18px !important; font-size: 0.95rem !important; width: 100% !important; box-sizing: border-box !important; }
          .ai-results-header h3 { font-size: 1.1rem !important; }
          .ai-grid-2 { grid-template-columns: 1fr !important; gap: 10px !important; }
          .diag-field-row { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
          .diag-field-row input { width: 100% !important; box-sizing: border-box !important; }
        }
      `}</style>

      {/* ── VALIDATION MODAL ── */}
      {validationModal && (
        <div
          onClick={() => setValidationModal(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '20px', padding: '28px 28px 24px', maxWidth: '440px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s ease-out' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ width: 40, height: 40, borderRadius: '12px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', flexShrink: 0 }}>⚠️</div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: '#0f172a' }}>Incomplete Data</h3>
                <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b' }}>Fill in the required fields before running analysis</p>
              </div>
            </div>
            <div style={{ background: '#fafafa', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8' }}>Required to proceed</p>
              {validationModal.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: i < validationModal.length - 1 ? '10px' : 0 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', marginTop: '6px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.88rem', color: '#1e293b', lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setValidationModal(null)}
              style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer' }}
            >
              Go Back &amp; Fill
            </button>
          </div>
        </div>
      )}

      {/* ── PRINT-ONLY SUMMARY ── */}
      {ReactDOM.createPortal(
        <div id="ortho-print-summary">
          <OrthoPrintSummary data={formData} diagnosisData={diagnosisData} zone={activeZone} isSubmitted={isSubmitted} />
        </div>,
        document.body
      )}

      {/* ── STICKY HEADER ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 200, backgroundColor: 'rgba(241,245,249,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${theme.border}` }}>
        <div className="ortho-content-pad" style={{ maxWidth: '1040px', margin: '0 auto', padding: '0 20px' }}>

          {/* nav row — back | stepper | print */}
          <header style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '56px' }}>
            {/* left: back button (fixed width so stepper stays centred) */}
            <div style={{ width: '44px', flexShrink: 0 }}>
              <button
                type="button"
                onClick={currentStep === 0 ? () => navigate('/home') : handleBack}
                aria-label="Back"
                style={{ minWidth: '44px', minHeight: '44px', padding: '6px 10px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: 'transparent', cursor: 'pointer', fontWeight: '700', fontSize: '1.1rem', color: theme.textMuted, lineHeight: 1, transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ←
              </button>
            </div>

            {/* centre: stepper (only on steps 1–4) */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
              {currentStep > 0 && STEPS.slice(1).map((step, i) => {
                const idx = i + 1;
                const isActive = currentStep === idx;
                const isDone   = currentStep > idx;
                return (
                  <React.Fragment key={step}>
                    {i > 0 && (
                      <div style={{ flex: 1, height: '2px', borderRadius: '99px', background: isDone ? theme.success : isActive ? theme.primary : theme.border, transition: 'background 0.3s' }} />
                    )}
                    <div className="ortho-step-node" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                      <div style={{
                        width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.65rem', fontWeight: '800', transition: 'all 0.25s',
                        background: isDone ? theme.success : isActive ? theme.primary : '#e2e8f0',
                        color: isDone || isActive ? '#fff' : '#94a3b8',
                        boxShadow: isActive ? `0 0 0 3px ${theme.primary}22` : 'none',
                        flexShrink: 0,
                      }}>
                        {isDone ? '✓' : idx}
                      </div>
                      <span className="ortho-step-label" style={{ fontSize: '0.6rem', fontWeight: isActive ? '700' : '500', color: isActive ? theme.primary : isDone ? theme.success : '#94a3b8', whiteSpace: 'nowrap' }}>
                        {step}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
              {currentStep === 0 && activeZone && (
                <span className="ortho-zone-badge" style={{ background: '#eff6ff', color: '#2563eb', padding: '3px 10px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: '700' }}>
                  {activeZone}
                </span>
              )}
            </div>

          </header>

          {/* print row — only on last step */}
          {currentStep === 4 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px' }}>
              <button type="button" onClick={() => navigate('/home')}
                style={{ padding: '5px 14px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.surface, cursor: 'pointer', fontWeight: '700', fontSize: '0.78rem', color: '#4f46e5', transition: 'background-color 0.15s, color 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.borderColor = '#a5b4fc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = theme.surface; e.currentTarget.style.borderColor = theme.border; }}>
                ⌂ Homepage
              </button>
              {isSubmitted && (
                <button type="button" onClick={() => window.print()}
                  style={{ padding: '5px 14px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.surface, cursor: 'pointer', fontWeight: '700', fontSize: '0.78rem', color: '#16a34a', transition: 'background-color 0.15s, color 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#86efac'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = theme.surface; e.currentTarget.style.borderColor = theme.border; }}>
                  <span style={{ fontSize: '1rem' }}>↓</span> PDF
                </button>
              )}
            </div>
          )}

        </div>
      </div>

      <div style={styles.container}>

        {/* STEP 0: ZONE SELECTOR */}
        {currentStep === 0 && (
          <div style={{ animation: 'fadeIn 0.3s' }}>
            <h3 className="ortho-zone-title" style={{ color: theme.textMuted, textAlign: 'center', marginBottom: '48px', fontWeight: '600', fontSize: '1.2rem' }}>Select Anatomical Zone to Begin</h3>
            {['Spine', 'Upper Limb', 'Lower Limb'].map(region => {
              const rmeta = REGION_META[region];
              const zones = Object.keys(REGIONAL_CONFIG).filter(k => REGIONAL_CONFIG[k].region === region);
              return (
                <div key={region} className="ortho-region-group" style={{ marginBottom: '48px' }}>
                  {/* Region group header */}
                  <div className="ortho-region-hdr" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div className="ortho-region-hdr-icon" style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${rmeta.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                      {rmeta.icon}
                    </div>
                    <h4 className="ortho-region-hdr-title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: theme.textMain, letterSpacing: '-0.01em' }}>{region}</h4>
                    <div style={{ flex: 1, height: '2px', backgroundColor: theme.border, borderRadius: '99px' }} />
                  </div>
                  {/* Zone cards */}
                  <div className="ortho-zone-grid" style={styles.zoneGrid}>
                    {zones.map(zoneKey => {
                      const zmeta = ZONE_META[zoneKey] || { icon: '🦴', color: '#6366f1' };
                      return (
                        <div key={zoneKey}
                          className="ortho-zone-card"
                          style={{ ...styles.zoneCard, ...(hoveredZone === zoneKey ? { transform: 'translateY(-6px)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', borderColor: zmeta.color } : {}) }}
                          onMouseEnter={() => setHoveredZone(zoneKey)}
                          onMouseLeave={() => setHoveredZone('')}
                          onClick={() => { setActiveZone(zoneKey); setCurrentStep(1); }}>
                          <div className="zone-icon-box" style={{ width: '60px', height: '60px', backgroundColor: `${zmeta.color}15`, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', marginBottom: '15px' }}>
                            {zmeta.icon}
                          </div>
                          <span className="zone-name" style={{ fontSize: '1.4rem', fontWeight: '800', color: theme.textMain }}>{zoneKey}</span>
                          <span className="zone-areas" style={{ fontSize: '0.95rem', color: theme.textMuted, fontWeight: '500' }}>{REGIONAL_CONFIG[zoneKey].areas}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* STEP 1: PATIENT PROFILE */}
      
{currentStep === 1 && (
  <div style={styles.sectionCard}>
    
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '28px',
        paddingBottom: '18px',
        borderBottom: '2px solid #f1f5f9'
      }}
    >
      <div
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '14px',
          background: 'linear-gradient(135deg,#4f46e5,#3b82f6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '22px',
          flexShrink: 0
        }}
      >
        👤
      </div>

      <div>
        <h3
          style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '800',
            color: theme.textMain,
            letterSpacing: '-0.02em'
          }}
        >
          Patient Identification
        </h3>

        <span
          style={{
            fontSize: '0.78rem',
            color: theme.textMuted,
            fontWeight: '600'
          }}
        >
          Enter patient demographics and registration details
        </span>
      </div>
    </div>

    <div style={styles.gridContainer}>
      
      <FormInput
        label="Full Name"
        value={formData.name}
        onChange={(e) =>
          handleChange({
            target: { name: 'name', value: e.target.value }
          })
        }
        placeholder="e.g. Jane Doe"
        required
      />

      <FormInput
        label="Age"
        type="number"
        value={formData.age}
        onChange={(e) =>
          handleChange({
            target: { name: 'age', value: e.target.value }
          })
        }
      />

      <div style={styles.inputGroup}>
        <label style={styles.label}>Gender</label>
        <select
          style={styles.input}
          name="gender"
          value={formData.gender}
          onChange={handleChange}
        >
          <option value="">Select</option>
          <option>Male</option>
          <option>Female</option>
        </select>
      </div>

      <FormInput
        label="Occupation"
        value={formData.occupation}
        onChange={(e) =>
          handleChange({
            target: { name: 'occupation', value: e.target.value }
          })
        }
        placeholder="e.g. Construction"
      />

      <div style={styles.inputGroup}>
        <label style={styles.label}>Dominant Hand</label>
        <select
          style={styles.input}
          name="dominantHand"
          value={formData.dominantHand}
          onChange={handleChange}
        >
          <option>Right</option>
          <option>Left</option>
          <option>Ambidextrous</option>
        </select>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Activity Level</label>
        <select
          style={styles.input}
          name="activityLevel"
          value={formData.activityLevel}
          onChange={handleChange}
        >
          <option>Sedentary</option>
          <option>Moderate</option>
          <option>Athletic</option>
        </select>
      </div>

    </div>
  </div>
)}
        {/* STEP 2: SUBJECTIVE */}
       {currentStep === 2 && (
  <div style={styles.sectionCard}>

    <h3 style={styles.sectionTitle}>💬 Subjective Interview ({activeZone})</h3>
    
    <FormInput label="Chief Complaint" value={formData.chiefComplaint} onChange={(e)=>handleChange({target:{name:'chiefComplaint', value:e.target.value}})} placeholder="e.g. Right knee pain for 3 weeks..." />

    <div style={{ marginTop: '20px' }}>
      <FormInput label="History of Present Illness (HPI)" type="textarea" value={formData.hpiText} onChange={(e)=>handleChange({target:{name:'hpiText', value:e.target.value}})} placeholder="• Hit enter for bullet points..." />
    </div>
    
    <div style={{...styles.gridContainer, marginTop: '24px'}}>
      <FormInput label="Past Medical History" value={formData.historyMedical} onChange={(e)=>handleChange({target:{name:'historyMedical', value:e.target.value}})} placeholder="Diabetes, Hypertension..." />
      <FormInput label="Lifestyle / Personal History" value={formData.historyPersonal} onChange={(e)=>handleChange({target:{name:'historyPersonal', value:e.target.value}})} placeholder="Diet, Smoking..." />
    </div>

    <div style={{marginTop: '24px', marginBottom: '10px'}}>
      <label style={{...styles.label, marginBottom: '10px'}}>Orthopedic History</label>
      <div style={styles.chipRow}>
        {PREVIOUS_HISTORY_OPTIONS.map(opt => <button key={opt} type="button" style={{...styles.chip, ...(formData.previousHistory.includes(opt) ? styles.chipWarning : {})}} onClick={() => toggleArrayItem('previousHistory', opt)}>{formData.previousHistory.includes(opt) ? '✓ ' : '+ '} {opt}</button>)}
      </div>
    </div>

    <div className="subj-section-gap" style={{ marginTop: '28px' }}>
      <h4 style={styles.subBoxTitle}>🚩 Red Flag Screening</h4>
      <div style={styles.chipRow}>
        {(ZoneConfig.redFlags || []).map(flag => <button key={flag} type="button" style={{...styles.chip, ...(formData.redFlags.includes(flag) ? styles.chipDanger : {})}} onClick={() => toggleArrayItem('redFlags', flag)}>{formData.redFlags.includes(flag) ? '✓ ' : '+ '} {flag}</button>)}
      </div>
    </div>

    <div className="subj-section-gap" style={{ marginTop: '24px' }}>
      <div className="subj-fl-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
        <h4 style={{...styles.subBoxTitle, margin: 0}}>📉 Subjective Functional Limitations</h4>
        <select style={{...styles.input, width: 'auto', padding: '10px 16px'}} name="functionalImpact" value={formData.functionalImpact} onChange={handleChange}>
          <option value="">Overall Impact...</option>{FUNCTIONAL_IMPACT_OPTIONS.map(o => <option key={o}>{o}</option>)}
        </select>
      </div>
      <div style={styles.chipRow}>
        {(ZoneConfig.functionalLimitations || []).map(limit => <button key={limit} type="button" style={{...styles.chip, ...(formData.functionalLimitations.includes(limit) ? styles.chipActive : {})}} onClick={() => toggleArrayItem('functionalLimitations', limit)}>{formData.functionalLimitations.includes(limit) ? '✓ ' : '+ '} {limit}</button>)}
      </div>
    </div>

    <div className="subj-section-gap" style={{ marginTop: '24px' }}>
      <h4 style={styles.subBoxTitle}>🔥 Comprehensive Pain Profile</h4>

      <div style={{marginBottom: '30px'}}>
        <div style={{display:'flex', justifyContent:'space-between', marginBottom: '12px'}}><label style={styles.label}>Pain Severity (VAS)</label><span style={{fontWeight:'800', fontSize:'1.2rem', color: parseInt(formData.symSeverity) >= 7 ? theme.danger : theme.primary}}>{formData.symSeverity}/10</span></div>
        <input type="range" min="0" max="10" style={{width:'100%', cursor:'pointer'}} value={formData.symSeverity} onChange={(e)=>handleChange({target:{name:'symSeverity', value:e.target.value}})} />
      </div>

      <div style={styles.gridContainer}>
        <div style={styles.inputGroup}><label style={styles.label}>Body Side</label>
          <div style={styles.chipRow}>{['Right', 'Left', 'Bilateral'].map(opt => <button key={opt} type="button" style={{...styles.chip, ...(formData.symSide===opt?styles.chipActive:{})}} onClick={()=>handleChange({target:{name:'symSide', value:opt}})}>{opt}</button>)}</div>
        </div>
        <div style={styles.inputGroup}><label style={styles.label}>Onset Type</label>
          <div style={styles.chipRow}>{['Gradual', 'Sudden', 'Trauma'].map(opt => <button key={opt} type="button" style={{...styles.chip, ...(formData.symOnset===opt?styles.chipActive:{})}} onClick={()=>handleChange({target:{name:'symOnset', value:opt}})}>{opt}</button>)}</div>
        </div>

        <FormInput label="Duration (Weeks)" type="number" value={formData.symDurationWeeks} onChange={(e)=>handleChange({target:{name:'symDurationWeeks', value:e.target.value}})} placeholder="e.g. 4" />
        <div style={styles.inputGroup}><label style={styles.label}>Symptom Progression</label><select style={styles.input} name="symptomProgression" value={formData.symptomProgression} onChange={handleChange}><option value="">Select...</option>{SYMPTOM_PROGRESSION_OPTIONS.map(o => <option key={o}>{o}</option>)}</select></div>
        <div style={styles.inputGroup}><label style={styles.label}>Pain Irritability</label><select style={styles.input} name="painIrritability" value={formData.painIrritability} onChange={handleChange}><option value="">Select...</option>{PAIN_IRRITABILITY_OPTIONS.map(o => <option key={o}>{o}</option>)}</select></div>
        <div style={styles.inputGroup}><label style={styles.label}>Nature of Pain</label><select style={styles.input} name="symNature" value={formData.symNature} onChange={handleChange}><option value="">Select...</option>{NATURE_OPTIONS.map(o => <option key={o}>{o}</option>)}</select></div>

        <div style={styles.inputGroup}><label style={styles.label}>Mechanism of Injury</label><select style={styles.input} name="mechanismOfInjury" value={formData.mechanismOfInjury} onChange={handleChange}><option value="">Select...</option>{(ZoneConfig.moiOptions || []).map(o => <option key={o}>{o}</option>)}</select></div>
        <div style={styles.inputGroup}><label style={styles.label}>Trauma Severity</label><select style={styles.input} name="traumaSeverity" value={formData.traumaSeverity} onChange={handleChange}><option value="">Select...</option>{TRAUMA_SEVERITY_OPTIONS.map(o => <option key={o}>{o}</option>)}</select></div>
        <div style={styles.inputGroup}><label style={styles.label}>Aggravating Factors</label><select style={styles.input} name="aggravating" value={formData.aggravating} onChange={handleChange}><option value="">Select...</option>{(ZoneConfig.aggravatingOptions || []).map(o => <option key={o}>{o}</option>)}</select></div>
        <div style={styles.inputGroup}><label style={styles.label}>Relieving Factors</label><select style={styles.input} name="relieving" value={formData.relieving} onChange={handleChange}><option value="">Select...</option>{(ZoneConfig.relievingOptions || []).map(o => <option key={o}>{o}</option>)}</select></div>
      </div>

      <div style={{marginTop: '30px'}}>
        <label style={{...styles.label, color: theme.primary, marginBottom: '10px', display: 'block'}}>Pain Distribution</label>
        <div style={{...styles.chipRow, marginBottom: '20px'}}>
          {PAIN_DISTRIBUTION_OPTIONS.map(opt => <button key={opt} type="button" style={{...styles.chip, ...(formData.painDistribution.includes(opt) ? styles.chipActive : {})}} onClick={() => toggleArrayItem('painDistribution', opt)}>{formData.painDistribution.includes(opt) ? '✓ ' : '+ '} {opt}</button>)}
        </div>

        <label style={{...styles.label, color: theme.primary, marginBottom: '10px', display: 'block'}}>Pain Behavior Pattern</label>
        <div style={{...styles.chipRow, marginBottom: '20px'}}>
          {PAIN_BEHAVIOR_OPTIONS.map(opt => <button key={opt} type="button" style={{...styles.chip, ...(formData.painBehavior.includes(opt) ? styles.chipActive : {})}} onClick={() => toggleArrayItem('painBehavior', opt)}>{formData.painBehavior.includes(opt) ? '✓ ' : '+ '} {opt}</button>)}
        </div>

        <label style={{...styles.label, color: theme.primary, marginBottom: '10px', display: 'block'}}>Morning Function</label>
        <div style={{...styles.chipRow, marginBottom: '20px'}}>
          {MORNING_FUNCTION_OPTIONS.map(opt => <button key={opt} type="button" style={{...styles.chip, ...(formData.morningFunction.includes(opt) ? styles.chipPurple : {})}} onClick={() => toggleArrayItem('morningFunction', opt)}>{formData.morningFunction.includes(opt) ? '✓ ' : '+ '} {opt}</button>)}
        </div>

        <label style={{...styles.label, color: theme.primary, marginBottom: '10px', display: 'block'}}>Instability & Mechanical Symptoms</label>
        <div style={{...styles.chipRow, marginBottom: '20px'}}>
          {INSTABILITY_SYMPTOMS_OPTIONS.map(opt => <button key={opt} type="button" style={{...styles.chip, ...(formData.instabilitySymptoms.includes(opt) ? styles.chipWarning : {})}} onClick={() => toggleArrayItem('instabilitySymptoms', opt)}>{formData.instabilitySymptoms.includes(opt) ? '✓ ' : '+ '} {opt}</button>)}
        </div>

        <label style={{...styles.label, color: theme.primary, marginBottom: '10px', display: 'block'}}>Subjective Inflammation Signs</label>
        <div style={{...styles.chipRow}}>
          {INFLAMMATION_SYMPTOMS_OPTIONS.map(opt => <button key={opt} type="button" style={{...styles.chip, ...(formData.inflammationSymptoms.includes(opt) ? styles.chipDanger : {})}} onClick={() => toggleArrayItem('inflammationSymptoms', opt)}>{formData.inflammationSymptoms.includes(opt) ? '✓ ' : '+ '} {opt}</button>)}
        </div>
      </div>

    </div>

  </div>
)}

        {/* STEP 3: OBJECTIVE */}
        {currentStep === 3 && (
          <div style={styles.sectionCard}>
            <h3 style={styles.sectionTitle}>🩺 Objective Examination ({activeZone})</h3>
             
             <div style={{ marginBottom: '24px' }}>
               <h4 style={styles.subBoxTitle}>👁 Visual Observations & Posture</h4>
               <div style={styles.chipRow}>
                 {(ZoneConfig.observations || []).map(obs => <button key={obs} type="button" style={{...styles.chip, ...(formData.observations.includes(obs) ? styles.chipActive : {})}} onClick={() => toggleArrayItem('observations', obs)}>{formData.observations.includes(obs) ? '✓ ' : '+ '} {obs}</button>)}
               </div>
             </div>

             <div style={{ marginBottom: '24px' }}>
               <h4 style={styles.subBoxTitle}>🚶 Gait Analysis</h4>
               <div style={styles.chipRow}>
                 {(ZoneConfig.gaitOptions || GAIT_OPTIONS).map(gait => <button key={gait} type="button" style={{...styles.chip, ...(formData.gait.includes(gait) ? (gait==='Normal' ? styles.chipSuccess : styles.chipWarning) : {})}} onClick={() => toggleArrayItem('gait', gait)}>{formData.gait.includes(gait) ? '✓ ' : '+ '} {gait}</button>)}
               </div>
             </div>

             <div style={{ marginBottom: '24px' }}>
               <h4 style={styles.subBoxTitle}>🖐 Palpation Findings</h4>
               <div style={styles.chipRow}>
                 {(ZoneConfig.palpationOptions || PALPATION_OPTIONS).map(palp => <button key={palp} type="button" style={{...styles.chip, ...(formData.palpation.includes(palp) ? styles.chipDanger : {})}} onClick={() => toggleArrayItem('palpation', palp)}>{formData.palpation.includes(palp) ? '✓ ' : '+ '} {palp}</button>)}
               </div>
             </div>

             <div style={{marginBottom: '40px'}}>
               <h4 style={{...styles.subBoxTitle, padding: '0 5px'}}>📐 Range of Motion</h4>
               {formData.rom_list.map(row => <SmartRow key={row.id} mode="ROM" data={row} config={ZoneConfig} onChange={(u) => updateRow('rom_list', row.id, u)} onDelete={() => deleteRow('rom_list', row.id)} />)}
               <button type="button" style={styles.addBtn} onClick={() => addRow('rom_list', {move:'', activeRom:'', passiveRom:'', pain:false, endFeel:''})}>+ Add Movement</button>
             </div>

             <div style={{marginBottom: '40px'}}>
               <h4 style={{...styles.subBoxTitle, padding: '0 5px'}}>🦴 Accessory Joint Motion (Glides)</h4>
               {formData.glide_list.map(row => <SmartRow key={row.id} mode="GLIDE" data={row} config={ZoneConfig} onChange={(u) => updateRow('glide_list', row.id, u)} onDelete={() => deleteRow('glide_list', row.id)} />)}
               <button type="button" style={{...styles.addBtn, color: theme.warning, borderColor: '#fde68a', backgroundColor: '#fffbeb'}} onClick={() => addRow('glide_list', {direction:'', mobility:'', symptom:''})}>+ Add Joint Glide</button>
             </div>

             <div style={{marginBottom: '40px'}}>
               <h4 style={{...styles.subBoxTitle, padding: '0 5px'}}>💪 Manual Muscle Testing</h4>
               {formData.mmt_list.map(row => <SmartRow key={row.id} mode="MMT" data={row} config={ZoneConfig} onChange={(u) => updateRow('mmt_list', row.id, u)} onDelete={() => deleteRow('mmt_list', row.id)} />)}
               <button type="button" style={{...styles.addBtn, color: '#a855f7', borderColor: '#e9d5ff', backgroundColor: '#faf5ff'}} onClick={() => addRow('mmt_list', {move:'', grade:''})}>+ Add Muscle Grade</button>
             </div>

             <div style={{marginBottom: '40px', border: `1px solid #fbcfe8`, borderRadius: '16px', overflow: 'hidden'}}>
               <button
                 type="button"
                 onClick={() => setShowNeuro(p => !p)}
                 style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: showNeuro ? '#fdf2f8' : '#fdf4ff', border: 'none', cursor: 'pointer', fontWeight: '800', fontSize: '0.95rem', color: '#be185d' }}
               >
                 <span>🧠 Neurological Screening {(formData.umnSigns.length > 0 || formData.neuro_list.length > 0) && <span style={{ fontSize: '0.72rem', fontWeight: '700', background: '#be185d', color: '#fff', borderRadius: '99px', padding: '2px 9px', marginLeft: '8px' }}>{formData.umnSigns.length + formData.neuro_list.length}</span>}</span>
                 <span style={{ fontSize: '1.1rem', transition: 'transform 0.2s', display: 'inline-block', transform: showNeuro ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
               </button>
               {showNeuro && (
                 <div style={{ padding: '20px 24px', backgroundColor: '#fdf2f8' }}>
                   <div style={{marginBottom: '20px'}}>
                     <label style={{...styles.label, color: '#9d174d'}}>Upper Motor Neuron (UMN) Signs</label>
                     <div style={{...styles.chipRow, marginTop: '8px'}}>
                       {(ZoneConfig.umnOptions || UMN_OPTIONS).map(umn => <button key={umn} type="button" style={{...styles.chip, ...(formData.umnSigns.includes(umn) ? styles.chipDanger : {})}} onClick={() => toggleArrayItem('umnSigns', umn)}>{formData.umnSigns.includes(umn) ? '✓ ' : '+ '} {umn}</button>)}
                     </div>
                   </div>
                   {formData.neuro_list.map(row => <SmartRow key={row.id} mode="NEURO" data={row} config={ZoneConfig} onChange={(u) => updateRow('neuro_list', row.id, u)} onDelete={() => deleteRow('neuro_list', row.id)} />)}
                   <button type="button" style={{...styles.addBtn, color: '#be185d', borderColor: '#fbcfe8', backgroundColor: '#fdf4ff'}} onClick={() => addRow('neuro_list', {root:'', dermatome:'Normal', myotome:'', reflex:''})}>+ Add Nerve Root Assessment</button>
                 </div>
               )}
             </div>

             <div style={{marginBottom: '40px'}}>
               <h4 style={{...styles.subBoxTitle, padding: '0 5px'}}>⏱ Objective Functional Tests</h4>
               {formData.functional_list.map(row => <SmartRow key={row.id} mode="FUNCTIONAL" data={row} config={ZoneConfig} onChange={(u) => updateRow('functional_list', row.id, u)} onDelete={() => deleteRow('functional_list', row.id)} />)}
               <button type="button" style={{...styles.addBtn, color: '#059669', borderColor: '#a7f3d0', backgroundColor: '#f0fdf4'}} onClick={() => addRow('functional_list', {test:'', result:''})}>+ Add Performance Score</button>
             </div>

             <div>
               <h4 style={{...styles.subBoxTitle, padding: '0 5px'}}>⚡ Special Orthopedic Tests</h4>
               {formData.special_list.map(row => <SmartRow key={row.id} mode="SPECIAL" data={row} config={ZoneConfig} onChange={(u) => updateRow('special_list', row.id, u)} onDelete={() => deleteRow('special_list', row.id)} />)}
               <button type="button" style={{...styles.addBtn, color: '#0284c7', borderColor: '#bae6fd', backgroundColor: '#f0f9ff'}} onClick={() => addRow('special_list', {test:'', result:''})}>+ Add Special Test Result</button>
             </div>

             {/* ── Collapsible Investigations & Diagnostics ── */}
             <div style={{ marginTop: '32px', border: '1px solid #c7d2fe', borderRadius: '16px', overflow: 'hidden' }}>
               <button
                 type="button"
                 onClick={() => setShowInvestigations(p => !p)}
                 style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: showInvestigations ? '#eef2ff' : '#f5f3ff', border: 'none', cursor: 'pointer', fontWeight: '800', fontSize: '0.95rem', color: '#4338ca' }}
               >
                 <span>🩻 Investigations &amp; Diagnostics {formData.investigation_list.length > 0 && <span style={{ fontSize: '0.72rem', fontWeight: '700', background: '#4f46e5', color: '#fff', borderRadius: '99px', padding: '2px 9px', marginLeft: '8px' }}>{formData.investigation_list.length}</span>}</span>
                 <span style={{ fontSize: '1.1rem', transition: 'transform 0.2s', display: 'inline-block', transform: showInvestigations ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
               </button>
               {showInvestigations && (
                 <div style={{ padding: '20px 20px 16px', background: '#fff' }}>
                   {formData.investigation_list.map(row => <SmartRow key={row.id} mode="INVESTIGATION" data={row} onChange={(u) => updateRow('investigation_list', row.id, u)} onDelete={() => deleteRow('investigation_list', row.id)} />)}
                   <button type="button" style={{...styles.addBtn, color: '#4f46e5', borderColor: '#c7d2fe', backgroundColor: '#eef2ff'}} onClick={() => addRow('investigation_list', {type:'', finding:''})}>+ Add Imaging or Lab Result</button>
                 </div>
               )}
             </div>
          </div>
        )}

        {/* STEP 4: AI DIAGNOSIS */}
        {currentStep === 4 && (
          <div style={styles.sectionCard}>

            {/* ── Header + Tab Switch ── */}
            <div style={{ borderBottom: `2px solid ${theme.secondary}`, paddingBottom: '20px', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: theme.textMain, margin: '0 0 16px' }}>✨ Diagnosis</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[['ai', '🤖 Diagnose with AI'], ['manual', '✏️ Manual Diagnosis']].map(([tab, label]) => (
                  <button key={tab} type="button" onClick={() => setDiagTab(tab)} style={{
                    padding: '10px 20px', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem',
                    cursor: 'pointer', border: 'none', transition: 'background-color 0.15s, color 0.15s, box-shadow 0.15s',
                    background: diagTab === tab ? theme.primary : '#f1f5f9',
                    color: diagTab === tab ? '#fff' : theme.textMuted,
                    boxShadow: diagTab === tab ? '0 2px 8px rgba(79,70,229,0.25)' : 'none',
                  }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* ══ AI TAB ══ */}
            {diagTab === 'ai' && (
              <div>
                {!isSubmitted ? (
                  <div className="ai-card-pre">
                    <h4 className="ai-pre-title">Clinical Data Collection Complete</h4>
                    <p className="ai-pre-desc">Run the AI Diagnostic Engine to generate primary impression, clinical reasoning, and management plan from the collected orthopaedic findings.</p>
                    <button type="button" onClick={handleFinalSubmit} disabled={isDiagnosing} className="ai-analyze-btn"
                      style={{ background: isDiagnosing ? '#94a3b8' : 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)', color: '#fff', border: 'none', borderRadius: '12px', cursor: isDiagnosing ? 'not-allowed' : 'pointer', fontWeight: '800', boxShadow: isDiagnosing ? 'none' : '0 10px 25px -5px rgba(79,70,229,0.4)', transition: 'all 0.2s' }}
                      onMouseEnter={(e) => !isDiagnosing && (e.currentTarget.style.transform = 'translateY(-2px)')}
                      onMouseLeave={(e) => !isDiagnosing && (e.currentTarget.style.transform = 'translateY(0)')}>
                      {isDiagnosing ? 'Diagnosing...' : 'Diagnose with AI ✨'}
                    </button>
                  </div>
                ) : (
                  <div style={{ animation: 'fadeIn 0.5s ease-out' }}>

                      {/* Header */}
                      <div className="ai-results-header">
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: theme.textMain, margin: 0 }}>🧠 AI Clinical Assessment</h3>
                        <span style={{ background: '#eff6ff', color: '#4f46e5', padding: '8px 16px', borderRadius: '99px', fontSize: '0.85rem', fontWeight: '800', border: '1px solid #c7d2fe' }}>{activeZone}</span>
                      </div>

                      {/* AI Disclaimer */}
                      <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '10px 16px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚠️</span>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#92400e', lineHeight: 1.55 }}>
                          <strong>AI-generated content — clinical review required.</strong> This assessment is produced by an AI model and may contain errors or omissions. It is intended to support, not replace, clinical judgement. Always verify findings against the patient's full clinical picture before acting.
                        </p>
                      </div>

                      {/* Primary Impression + Treatment Phase */}
                      <div style={{ marginBottom: '20px', padding: '18px 20px', borderRadius: '14px', backgroundColor: '#eff6ff', border: '2px solid #bfdbfe' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.08em' }}>🎯 Primary Impression</div>
                          {diagnosisData.treatmentPhase && (
                            <span style={{ fontSize: '0.72rem', fontWeight: '800', padding: '3px 10px', borderRadius: '99px', background: diagnosisData.treatmentPhase === 'Acute' ? '#fef2f2' : diagnosisData.treatmentPhase === 'Subacute' ? '#fffbeb' : '#f0fdf4', color: diagnosisData.treatmentPhase === 'Acute' ? '#991b1b' : diagnosisData.treatmentPhase === 'Subacute' ? '#92400e' : '#065f46', border: `1px solid ${diagnosisData.treatmentPhase === 'Acute' ? '#fca5a5' : diagnosisData.treatmentPhase === 'Subacute' ? '#fcd34d' : '#6ee7b7'}` }}>
                              {diagnosisData.treatmentPhase} Phase
                            </span>
                          )}
                        </div>
                        <p style={{ fontWeight: '700', fontSize: '1.15rem', color: '#1e40af', margin: 0 }}>{diagnosisData.primary || (typeof formData.diagnosis === 'string' ? formData.diagnosis : 'Analyzing...')}</p>
                        {diagnosisData.prognosisTimeline && <p style={{ margin: '8px 0 0', fontSize: '0.82rem', color: '#3b82f6', fontStyle: 'italic' }}>📅 {diagnosisData.prognosisTimeline}</p>}
                      </div>

                      {/* Clinical Reasoning */}
                      {!!diagnosisData.clinicalReasoning?.length && (
                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{ marginTop: 0, color: theme.textMain, fontSize: '1.1rem', fontWeight: '800', marginBottom: '12px' }}>🧩 Clinical Reasoning</h4>
                          <ul style={{ paddingLeft: '22px', marginBottom: 0, lineHeight: 1.7, color: theme.textMuted, fontSize: '0.97rem' }}>
                            {diagnosisData.clinicalReasoning.map((point, i) => <li key={i} style={{ marginBottom: '8px' }}>{point}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Physio Plan + Key Exercises */}
                      {(diagnosisData.physiotherapyManagementPlan?.length > 0 || diagnosisData.keyExercises?.length > 0) && (
                        <div className="ai-grid-2">
                          {diagnosisData.physiotherapyManagementPlan?.length > 0 && (
                            <div style={{ padding: '20px', borderRadius: '14px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                              <h4 style={{ marginTop: 0, color: '#15803d', fontSize: '1rem', fontWeight: '800', marginBottom: '12px' }}>🛠 Physiotherapy Plan</h4>
                              <ul style={{ paddingLeft: '18px', marginBottom: 0, color: theme.textMuted, lineHeight: 1.6, fontSize: '0.95rem' }}>
                                {diagnosisData.physiotherapyManagementPlan.map((item, i) => <li key={i} style={{ marginBottom: '6px' }}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                          {diagnosisData.keyExercises?.length > 0 && (
                            <div style={{ padding: '20px', borderRadius: '14px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
                              <h4 style={{ marginTop: 0, color: '#c2410c', fontSize: '1rem', fontWeight: '800', marginBottom: '12px' }}>🏃 Key Exercises</h4>
                              <ul style={{ paddingLeft: '18px', marginBottom: 0, color: theme.textMuted, lineHeight: 1.6, fontSize: '0.95rem' }}>
                                {diagnosisData.keyExercises.map((item, i) => <li key={i} style={{ marginBottom: '6px' }}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Short-term + Long-term Goals */}
                      {(diagnosisData.shortTermGoals?.length > 0 || diagnosisData.longTermGoals?.length > 0) && (
                        <div className="ai-grid-2">
                          {diagnosisData.shortTermGoals?.length > 0 && (
                            <div style={{ padding: '20px', borderRadius: '14px', backgroundColor: '#faf5ff', border: '1px solid #e9d5ff' }}>
                              <h4 style={{ marginTop: 0, color: '#7c3aed', fontSize: '1rem', fontWeight: '800', marginBottom: '12px' }}>🎯 Short-Term Goals <span style={{ fontSize: '0.72rem', fontWeight: '600', color: '#9d74d8' }}>(2–4 wks)</span></h4>
                              <ul style={{ paddingLeft: '18px', marginBottom: 0, color: theme.textMuted, lineHeight: 1.6, fontSize: '0.95rem' }}>
                                {diagnosisData.shortTermGoals.map((item, i) => <li key={i} style={{ marginBottom: '6px' }}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                          {diagnosisData.longTermGoals?.length > 0 && (
                            <div style={{ padding: '20px', borderRadius: '14px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                              <h4 style={{ marginTop: 0, color: '#1d4ed8', fontSize: '1rem', fontWeight: '800', marginBottom: '12px' }}>🏁 Long-Term Goals <span style={{ fontSize: '0.72rem', fontWeight: '600', color: '#3b82f6' }}>(8–12 wks)</span></h4>
                              <ul style={{ paddingLeft: '18px', marginBottom: 0, color: theme.textMuted, lineHeight: 1.6, fontSize: '0.95rem' }}>
                                {diagnosisData.longTermGoals.map((item, i) => <li key={i} style={{ marginBottom: '6px' }}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Precautions */}
                      {diagnosisData.precautions?.length > 0 && (
                        <div style={{ padding: '18px 20px', borderRadius: '14px', backgroundColor: '#fef2f2', border: '2px solid #fca5a5', marginBottom: '20px' }}>
                          <h4 style={{ color: '#991b1b', marginTop: 0, fontSize: '1rem', fontWeight: '800', marginBottom: '10px' }}>🚫 Treatment Precautions</h4>
                          <ul style={{ color: '#7f1d1d', paddingLeft: '20px', margin: 0, lineHeight: 1.7, fontSize: '0.88rem' }}>
                            {diagnosisData.precautions.map((item, i) => <li key={i}>{item}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Patient Education + Outcome Measures */}
                      {(diagnosisData.patientEducation?.length > 0 || diagnosisData.outcomeMeasures?.length > 0) && (
                        <div className="ai-grid-2">
                          {diagnosisData.patientEducation?.length > 0 && (
                            <div style={{ padding: '20px', borderRadius: '14px', backgroundColor: '#fefce8', border: '1px solid #fde68a' }}>
                              <h4 style={{ marginTop: 0, color: '#92400e', fontSize: '1rem', fontWeight: '800', marginBottom: '12px' }}>📚 Patient Education</h4>
                              <ul style={{ paddingLeft: '18px', marginBottom: 0, color: theme.textMuted, lineHeight: 1.6, fontSize: '0.92rem' }}>
                                {diagnosisData.patientEducation.map((item, i) => <li key={i} style={{ marginBottom: '6px' }}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                          {diagnosisData.outcomeMeasures?.length > 0 && (
                            <div style={{ padding: '20px', borderRadius: '14px', backgroundColor: '#f8fafc', border: `1px solid ${theme.border}` }}>
                              <h4 style={{ marginTop: 0, color: theme.textMain, fontSize: '1rem', fontWeight: '800', marginBottom: '12px' }}>📊 Outcome Measures</h4>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {diagnosisData.outcomeMeasures.map((item, i) => (
                                  <span key={i} style={{ fontSize: '0.82rem', fontWeight: '700', padding: '4px 12px', borderRadius: '99px', background: '#e0e7ff', color: '#3730a3' }}>{item}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Refine Button */}
                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                        <button type="button" onClick={handleRefineDiagnosis} disabled={isDiagnosing}
                          style={{ padding: '14px 28px', background: isDiagnosing ? '#94a3b8' : 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)', color: '#fff', border: 'none', borderRadius: '12px', cursor: isDiagnosing ? 'not-allowed' : 'pointer', fontWeight: '800', fontSize: '1rem', boxShadow: isDiagnosing ? 'none' : '0 10px 25px -5px rgba(79,70,229,0.4)', transition: 'all 0.2s' }}
                          onMouseEnter={(e) => !isDiagnosing && (e.currentTarget.style.transform = 'translateY(-2px)')}
                          onMouseLeave={(e) => !isDiagnosing && (e.currentTarget.style.transform = 'translateY(0)')}>
                          {isDiagnosing ? 'Refining...' : '🔄 Refine Diagnosis with Additional Context'}
                        </button>
                      </div>

                      {/* Chat */}
                      <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: '25px' }}>
                        <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: theme.textMain, marginBottom: '15px' }}>💬 Ask AI about this Case</h4>
                        <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '20px', minHeight: '100px', marginBottom: '15px', border: `1px solid ${theme.border}`, maxHeight: '300px', overflowY: 'auto' }}>
                          {chatHistory.map((msg, i) => (
                            <div key={i} style={{ marginBottom: '16px', lineHeight: 1.7, fontSize: '0.95rem' }}>
                              <strong style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: msg.role === 'user' ? '#4f46e5' : '#059669' }}>{msg.role === 'user' ? 'You' : 'PhysioAI'}</strong>
                              {msg.role === 'user' ? (
                                <div style={{ background: '#eff6ff', padding: '12px 16px', borderRadius: '0 12px 12px 12px', display: 'inline-block', color: theme.textMain }}>{msg.text}</div>
                              ) : (
                                <div style={{ marginTop: '4px', color: theme.textMain }}>{renderAiNotes(msg.text)}</div>
                              )}
                            </div>
                          ))}
                          {chatHistory.length === 0 && <p style={{ color: theme.textMuted, fontStyle: 'italic', margin: 0 }}>Ask about treatment plan, progression, or test validity...</p>}
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <input style={{ ...styles.input, backgroundColor: '#fff' }} value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="Ask about treatment plan, progression, or test validity..."
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleChatQuery(); } }} />
                          <button type="button" onClick={handleChatQuery} disabled={chatLoading || !chatInput.trim()}
                            style={{ ...styles.navBtn, ...styles.btnPrimary, padding: '14px 24px', opacity: chatLoading || !chatInput.trim() ? 0.7 : 1, cursor: chatLoading || !chatInput.trim() ? 'not-allowed' : 'pointer' }}>
                            {chatLoading ? 'Asking...' : 'Ask AI'}
                          </button>
                        </div>
                      </div>

                  </div>
                )}
              </div>
            )}

            {/* ══ MANUAL TAB ══ */}
            {diagTab === 'manual' && (
              <div>
                {aiAutoFilled && (
                  <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '12px', padding: '10px 16px', marginBottom: '24px', fontSize: '0.85rem', color: '#065f46', fontWeight: '600' }}>
                    ✅ AI has pre-filled the diagnosis. You can edit it below.
                  </div>
                )}

                {/* Differential Diagnosis */}
                <div style={{ marginBottom: '28px' }}>
                  <div className="diag-field-row" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <label style={{ fontSize: '1.05rem', fontWeight: '800', color: theme.textMain, whiteSpace: 'nowrap' }}>▪ Differential Diagnosis</label>
                    <input
                      type="text"
                      value={formData.differentialDiagnosis || ''}
                      onChange={(e) => handleChange({ target: { name: 'differentialDiagnosis', value: e.target.value } })}
                      placeholder="e.g. Rotator cuff tear, Subacromial impingement, ACJ pathology..."
                      style={{ ...styles.input, flex: 1 }}
                    />
                  </div>
                </div>

                {/* Goals */}
                <div style={{ marginBottom: '28px' }}>
                  <h4 style={{ margin: '0 0 14px 0', fontSize: '1.05rem', fontWeight: '800', color: theme.textMain }}>▪ Goals</h4>
                  <div style={{ border: `1px solid ${theme.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                    {[['shortTermGoals', 'Short Term Goals'], ['longTermGoals', 'Long Term Goals']].map(([field, lbl], idx) => (
                      <div key={field} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', borderTop: idx === 0 ? 'none' : `1px solid ${theme.border}` }}>
                        <div style={{ padding: '14px 16px', fontSize: '0.85rem', fontWeight: '700', color: theme.textMain, background: '#f8fafc', borderRight: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', lineHeight: 1.4 }}>{lbl}</div>
                        <textarea
                          value={formData[field] || ''}
                          onChange={(e) => handleChange({ target: { name: field, value: e.target.value } })}
                          placeholder={`Enter ${lbl.toLowerCase()}...`}
                          style={{ border: 'none', outline: 'none', resize: 'none', padding: '12px 14px', fontSize: '0.85rem', fontFamily: 'inherit', minHeight: '70px', background: 'transparent', color: theme.textMain, lineHeight: 1.6 }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Treatment Plan */}
                <div style={{ marginBottom: '8px' }}>
                  <h4 style={{ margin: '0 0 14px 0', fontSize: '1.05rem', fontWeight: '800', color: theme.textMain }}>▪ Treatment Plan</h4>
                  <textarea
                    value={formData.treatmentPlan || ''}
                    onChange={(e) => handleChange({ target: { name: 'treatmentPlan', value: e.target.value } })}
                    placeholder="Outline the physiotherapy treatment plan..."
                    style={{ ...styles.input, minHeight: '160px', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

          </div>
        )}

        {/* FIXED BOTTOM FOOTER */}
        {currentStep > 0 && (
          <div style={styles.footerBar}>
            <div style={styles.footerInner}>
              {currentStep < 4 && (
                <button type="button" onClick={handleNext} style={styles.footerBtn}>
                  Continue to {STEPS[currentStep + 1]} →
                </button>
              )}
              {currentStep === 4 && (
                <button type="button" onClick={handleSaveAndReturnHome} style={styles.footerBtnSuccess}>
                  Save & Return Home
                </button>
              )}
            </div>
          </div>
        )}

      </div>
    </div>

    {payGate && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 20, padding: '28px 28px 22px', maxWidth: 380, width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: '1.5rem', textAlign: 'center', marginBottom: 10 }}>🔬</div>
          <h3 style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', textAlign: 'center' }}>Unlock AI Diagnosis</h3>
          <p style={{ margin: '0 0 18px', fontSize: '0.82rem', color: '#64748b', textAlign: 'center', lineHeight: 1.5 }}>
            Your 3 free assessments are used up. Pay <strong>₹19</strong> for this session — unlocks AI diagnosis, refine, chat, and PDF download.
          </p>
          <button
            type="button"
            onClick={() => { setPayGateError(null); payForDiagnosis(payGate.assessmentId, {
              onSuccess: () => { setPayGate(null); handleFinalSubmit(); },
              onCancel:  () => setPayGate(null),
              onError:   (msg) => setPayGateError(msg || 'Payment failed. Please try again.'),
            }); }}
            style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg,#4f46e5,#3b82f6)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 10 }}
          >
            Pay ₹19 with Razorpay
          </button>
          {payGateError && (
            <div style={{ marginBottom: 10, padding: '9px 13px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fca5a5', fontSize: '0.76rem', color: '#991b1b', fontWeight: 600 }}>⚠ {payGateError}</div>
          )}
          <button
            type="button"
            onClick={() => { setPayGate(null); setPayGateError(null); }}
            style={{ width: '100%', padding: '11px', background: 'transparent', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default UnifiedOrthoForm;